import { ChildComponent } from "./child-component";
import { transient, autoinject } from "aurelia-framework";
import { NotificationClientService, NotificationType } from "../../../services/basics/notification-client.service";
import { I18N } from "aurelia-i18n";
import { EntityDynamicPropertiesService } from "../../../services/basics/entity-dynamic-properties.service";
import { DynamicColumnModel, EntityDynamicValue, EntityTypeConfigurationModel } from "../../../models/basics/basics.models";
import { GetDynamicPropertiesResponse } from "../../../models/manage/entities.models";
import { Router } from "aurelia-router";
import { BindingSignaler } from "aurelia-templating-resources";
import { GenesisRouterConstants } from "../../../configuration/router.constants";
import { UserClaimsService } from "../../../services/authentication/user-claims.service";
import { BaseClaims } from "../../../constants/authentication/base-claims";
import { DataType } from "../../../models/queries/queries.models";
import { LookupClientService } from "../../../services/lookups/lookup-client.service";
import { LookupValueRequest, LookupValue } from "../../../models/lookups/lookup.models";
import { ValidationActivation, RulesHolder } from "../../../client-validation-system/validations.models";
import { EntityValidationsService, ClientSideValidator } from "../../../client-validation-system/validations-service";
import * as Enumerable from "linq";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";

@autoinject()
@transient()
export class DynamicPropertiesManager extends ChildComponent<any> {

    private _orginalEntityDynamicValues: EntityDynamicValue[] = [];

    public entityDynamicValues: EntityDynamicValue[] = [];
    public dynamicColumns: { [prop: number]: DynamicColumnModel } = {};
    public canAccessManageEntities: boolean;
    public readonly: boolean = false;
    public manageEntityGroups: LookupValue[] = [];
    public usedManageEntityGroups: LookupValue[];
    public response: GetDynamicPropertiesResponse;
    public hasSave: boolean;

    public constructor(private entityDynamicPropertiesService: EntityDynamicPropertiesService,
        private router: Router,
        private userClaimsService: UserClaimsService,
        private entityValidationsService: EntityValidationsService,
        notificationService: NotificationClientService,
        private bindingSignaler: BindingSignaler,
        i18N: I18N, private lookupClientService: LookupClientService, private configService: ConfigService) {
        super(notificationService, i18N);
        this.canAccessManageEntities = this.userClaimsService.hasPermission(BaseClaims.CanAccessManageEntities);
    }

    onActivated() {
        const request = new LookupValueRequest();
        request.lookupValueQuery = "ManageEntityGroups";

        const lookupPromise = this.lookupClientService.getLookup(request);
        const dynamicProperyPromise =  this.entityDynamicPropertiesService.getDynamicPropertiesForEntity(this.item.id, this.item.entityType);

        Promise.all([lookupPromise, dynamicProperyPromise]).then(x => {
            this.manageEntityGroups = x[0].allValuesIncludingInactive.concat(<LookupValue>{ id: null });
            this.onDynamicPropertiesFetched(x[1]);
        });

        if (this.hasSave === undefined) {
            this.hasSave = this.item.hasSave;
        }
    }

    getEntityDynamicValues(groupId: number) {
        const columnIds = this.response.dynamicColumns.filter(x => x.manageEntityGroupId == groupId).map(x => x.columnId);
        return Enumerable.from(this.entityDynamicValues).orderBy(x => x.dynamicColumn.sequenceNumber).where(x => columnIds.filter(y => y == x.columnId).length > 0).toArray();
    }

    goToManageEntities() {
        this.router.navigateToRoute(GenesisRouterConstants.RouteNames.ManageEntities, { entityTypeId: this.item.entityType });
    }

    isTextBox(entityDynamicValue: EntityDynamicValue) {
        return this.dynamicColumns[entityDynamicValue.columnId].tableColumnDataTypeId == DataType.Default ||
            this.dynamicColumns[entityDynamicValue.columnId].tableColumnDataTypeId == DataType.Number ||
            this.dynamicColumns[entityDynamicValue.columnId].tableColumnDataTypeId == DataType.Double;
    }

    getTextBoxType(entityDynamicValue: EntityDynamicValue) {
        if (this.dynamicColumns[entityDynamicValue.columnId].tableColumnDataTypeId == DataType.Default) {
            return "text";
        }
        if (this.dynamicColumns[entityDynamicValue.columnId].tableColumnDataTypeId == DataType.Number) {
            return "integer";
        }
        if (this.dynamicColumns[entityDynamicValue.columnId].tableColumnDataTypeId == DataType.Double) {
            return "double";
        }
    }

    isCheckbox(entityDynamicValue: EntityDynamicValue) {
        return this.dynamicColumns[entityDynamicValue.columnId].tableColumnDataTypeId == DataType.Boolean;
    }

    isLookup(entityDynamicValue: EntityDynamicValue) {
        return this.dynamicColumns[entityDynamicValue.columnId].tableColumnDataTypeId == DataType.Lookup;
    }

    isHierarchyLookup(entityDynamicValue: EntityDynamicValue) {
        return this.dynamicColumns[entityDynamicValue.columnId].tableColumnDataTypeId == DataType.HierarchicalLookup;
    }

    isDate(entityDynamicValue: EntityDynamicValue) {
        return this.dynamicColumns[entityDynamicValue.columnId].tableColumnDataTypeId == DataType.DateTime ||
            this.dynamicColumns[entityDynamicValue.columnId].tableColumnDataTypeId == DataType.Date;
    }

    isEntityFinder(entityDynamicValue: EntityDynamicValue) {
        return this.dynamicColumns[entityDynamicValue.columnId].tableColumnDataTypeId == DataType.EntityFinder;
    }

    getDateFormat(entityDynamicValue: EntityDynamicValue) {
        return this.dynamicColumns[entityDynamicValue.columnId].tableColumnDataTypeId == DataType.Date ? this.configService.config.common.defaultDateFormat : this.configService.config.common.defaultDateTimeFormat;
    }

    hasTimezone(entityDynamicValue: EntityDynamicValue) {
        return this.dynamicColumns[entityDynamicValue.columnId].tableColumnDataTypeId == DataType.Date ? false : true;
    }

    getEntityFinderSupportedTypes(uri: string) {

        const config: EntityTypeConfigurationModel = {
            uri: uri,
            claims: [],
            excludeInDashboard: () => true,
            isVisible: true,
            format: "{0}",
            properties: "name"
        };

        return [config];
    }

    save(entityId: number) {
        return new Promise<void>((resolve, reject) => {
            if (entityId > 0) {

                const entityWithDynamicValues = {};
                this.response.entityDynamicValues.forEach(entityDynamicValue => {
                    const column = this.response.dynamicColumns.filter(x => x.columnId == entityDynamicValue.columnId)[0].columnName;
                    entityWithDynamicValues[column] = entityDynamicValue.castedValue;
                });

                const validator = this.getValidator(entityWithDynamicValues);

                // If validator is null, it means save is called without having the manager activated first.
                if (validator == null) {
                    resolve();
                }

                validator.validate({
                    object: entityWithDynamicValues
                }).then(validResponse => {
                    if (validResponse.valid) {
                        this.entityDynamicPropertiesService.saveDynamicPropertiesForEntity(this.entityDynamicValues, true).then(response => {
                            this.onDynamicPropertiesFetched(response);

                            this.notificationService.show(this.i18N.tr('changes-saved-successfully-text'), '', NotificationType.Success);
                            resolve();
                        }).catch(reject);

                    } else {
                        validResponse.results.forEach(res => {
                            //Add error manually to trigger UI validations.
                            validator.addError(res.message, this.response.entityDynamicValues.filter(x => x.dynamicColumn.columnName == res.propertyName)[0], "castedValue");
                        });
                        reject();
                    }
                });
            } else {
                resolve();
            }
        });
    }

    public onDynamicPropertiesFetched(response: GetDynamicPropertiesResponse) {

        this.response = response;

        const groupIds = response.dynamicColumns.map(x => x.manageEntityGroupId);
        this.usedManageEntityGroups = this.manageEntityGroups.filter(x => groupIds.filter(y => y == x.id).length > 0);

        response.dynamicColumns.forEach(column => {
            this.dynamicColumns[column.columnId] = column;
        });
        this.entityDynamicValues = response.entityDynamicValues ? response.entityDynamicValues : [];

        this.bindingSignaler.signal('refresh-dynamic-values');
        
        this._orginalEntityDynamicValues = CloneHelper.clone(this.entityDynamicValues);
    }

    public getChangedEntityDynamicValues() {
        const result = this.entityDynamicValues.filter(entityDynamicValue => {

            const originalEntityDynamicValue = this._orginalEntityDynamicValues.filter(x => x.columnId == entityDynamicValue.columnId)[0];

            if (originalEntityDynamicValue.dynamicColumn.tableColumnDataTypeId == DataType.Date || 
                originalEntityDynamicValue.dynamicColumn.tableColumnDataTypeId == DataType.DateTime) {
                    if (new Date(originalEntityDynamicValue.castedValue).toJSON() != new Date(entityDynamicValue.castedValue).toJSON()) {
                        return true;
                    }
                    else {
                        return false;
                    }
                }

            if (originalEntityDynamicValue.castedValue != entityDynamicValue.castedValue) {
                return true;
            }
            else return false;

        });

        return result;
    }

    public getValidator(item: any): ClientSideValidator {
        if (!this.response) {
            return null;
        }
        const rules: RulesHolder = {
            activation: ValidationActivation.Always,
            entityName: "entityWithDynamicValues",
            isCollection: false,
            nestedRulesHolder: [],
            rules: Enumerable.from(this.response.validationRules)
                .select(p => ({ errorMessage: p.errorMessage, parameters: p.parameters, validationType: p.validationType, property: p.property }))
                .toArray()
        };

        return this.entityValidationsService.getValidator(item, rules);
    }
}
